import { createDate, format, instantDate } from './date-fns';

export * from './common';
export * from './components';
export * from './formatSecondsToHoursMinutes';
export * from './formatTimeRemaining';
export * from './date-fns';
export { default as getTimezones } from './getTimezones';

// Not based on preference:
export const formatDate = format('yyyy-MM-dd');
export const formatTime = format('HH:mm');
export const formatDateTime = format('yyyy-MM-dd HH:mm:ss');
/**
 * Serializes a createDate() WALL-TIME date back to ISO with the profile offset: the legacy way wall
 * times are sent to the API. Not for instants (serialize those with `.toISOString()`).
 * The offset is looked up at the wall time's shifted epoch, so it is exact only when the device zone
 * equals the profile zone, and even then not inside the repeated fall-back hour; with another device
 * zone it can be 1h off for hours around either DST change. Prefer the raw API string.
 */
export const formatIso = format("yyyy-MM-dd'T'HH:mm:ssxxx");
export const formatFullDate = format('dd.MM.yyyy');

// Preference-based:
export const formatTeacherTime = format('p');
export const formatDateTimeHumanFriendly = format('LLLL d yyyy, p');

export const formatDayMonthYear = format('d LLLL yyyy');
export const formatDateDMY = format('d MMM yyyy');
export const formatMonthYear = format('MMMM yyyy');

/** GMT label templates: 'O' and '(O)' print "GMT+2" / "(GMT+2)", 'OOOO' the long form "GMT+02:00". */
type TimezoneTemplate = 'O' | '(O)' | 'OOOO';

/**
 * The GMT label of the profile timezone at this exact instant, e.g. "GMT+2" / "(GMT+2)" / "GMT+02:00".
 * Takes the raw ISO string (not a Date) so a createDate() wall time can't reach the offset token
 * and print the wrong zone near DST changes. For a displayed time range use createDateRange().timezone.
 */
export const formatTimezone = (isoDate: string, template: TimezoneTemplate = 'O') =>
    format(template, instantDate(isoDate));

/**
 * An ISO date made safe for a URL query param: the same instant in Z form
 * ('2026-09-30T14:00:00+00:00' → '2026-09-30T14:00:00.000Z'). An unencoded '+' of an offset
 * decodes to a space in the query string and breaks the date.
 */
export const fixUrlEncodedIsoDate = (urlIsoDate: string): string =>
    instantDate(urlIsoDate).toISOString();

/**
 * The GMT label of the profile timezone right now, e.g. "GMT+2".
 * Use it for "now" labels instead of formatTimezone(new Date().toISOString()).
 */
export const formatCurrentTimezone = (template: TimezoneTemplate = 'O') =>
    format(template, new Date());

/**
 * The profile timezone's UTC offset ('+02:00') at this exact instant, to pass as createDate()'s `tz`.
 * The offset token needs the instant (instantDate), never a createDate() wall time.
 * date-fns-tz getTimezoneOffset() and formatInTimeZone() are off near DST changes, so don't swap them in.
 */
const getProfileUtcOffset = (isoDate: string) => format('xxx', instantDate(isoDate));

/**
 * A time range to display: the start, the end and ONE label, all read on the clock the range
 * starts on (the profile zone's UTC offset at the start). Show the label once for the whole range.
 * Across a DST change a 1h class starting 02:00 CEST shows "02:00 - 03:00 (GMT+2)"; reading the
 * end on its own clock would show "02:00 - 02:00". Outside a DST change both clocks are the same.
 * ponytail: like any createDate() wall time, an end falling into the browser zone's own
 * spring-forward gap shifts by the gap (when the browser zone equals the profile zone that happens
 * to be the real clock time; otherwise the end is simply off by the gap); fixing that needs
 * string-level formatting instead of wall-time Dates.
 */
export const createDateRange = (
    startIsoDate: string,
    endIsoDate: string,
    template: TimezoneTemplate = 'O',
) => {
    return {
        startDate: createDate(startIsoDate),
        // the end on the start's clock, even if the profile zone changes offset before it
        endDate: createDate(endIsoDate, getProfileUtcOffset(startIsoDate)),
        timezone: formatTimezone(startIsoDate, template),
    };
};
